import React, { useState, useEffect } from 'react';
import { View, Text, ScrollView, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { API_ENDPOINTS } from '../config/api';

const categories = [
  'investment_strategies',
  'tax_planning', 
  'risk_management',
  'retirement_planning',
  'estate_planning',
  'portfolio_management',
  'alternative_investments',
  'behavioral_finance',
  'market_analysis',
  'wealth_preservation'
];

const KnowledgeAdmin = () => {
  // Admin access control - only allow in development mode
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  const [formData, setFormData] = useState({
    category: 'investment_strategies',
    title: '',
    content: '',
    advisorName: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [knowledgeStats, setKnowledgeStats] = useState(null);
  const [aiResponse, setAiResponse] = useState(null);

  useEffect(() => {
    loadKnowledgeStats();
  }, []);

  const loadKnowledgeStats = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.KNOWLEDGE.CATEGORIES);
      const data = await response.json();
      
      if (data.success) {
        setKnowledgeStats({
          categories: data.categories,
          totalCategories: data.categories.length
        });
      }
    } catch (error) {
      console.error('Error loading knowledge stats:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.content) {
      setMessage('❌ Please fill in title and content');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(API_ENDPOINTS.KNOWLEDGE.STORE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setMessage(`✅ Successfully stored: "${formData.title}"`);
        setFormData({
          category: 'investment_strategies',
          title: '',
          content: '',
          advisorName: ''
        });
        loadKnowledgeStats(); // Refresh stats
      } else {
        setMessage(`❌ Error: ${data.error}`);
      }
    } catch (error) {
      setMessage(`❌ Network error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const testAdvice = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.KNOWLEDGE.ADVICE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: 'demo_user',
          query: 'How should I invest my money for retirement?'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        console.log('🤖 Enhanced AI Response:', data);
        setAiResponse(data);
        setMessage(`✅ AI Test Complete - Sources: ${data.sources.length}, Confidence: ${data.confidence}`);
      } else {
        setMessage(`❌ AI Test Failed: ${data.error}`);
        setAiResponse(null);
      }
    } catch (error) {
      setMessage(`❌ AI Test Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Restrict access to development mode only
  if (!isDevelopment) {
    return (
      <View style={styles.container}>
        <View style={styles.accessDenied}>
          <Text style={styles.accessDeniedText}>🔒 Access Restricted</Text>
          <Text style={styles.accessDeniedSubtext}>
            Knowledge Base Administration is only available in development mode.
          </Text>
        </View>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.title}>🧠 AI Knowledge Base Admin</Text>
      
      {/* Knowledge Stats */}
      {knowledgeStats && (
        <View style={styles.statsCard}>
          <Text style={styles.cardTitle}>Knowledge Base Status</Text>
          <Text style={styles.statText}>
            Categories: {knowledgeStats.totalCategories}
          </Text>
          <Text style={styles.statText}>
            Active Categories: {knowledgeStats.categories.join(', ') || 'None'}
          </Text>
        </View>
      )}
      
      {/* Upload Form */}
      <View style={styles.formCard}>
        <Text style={styles.cardTitle}>Add Expert Knowledge</Text>
        
        <View style={styles.inputGroup}>
          <Text style={styles.label}>Category</Text>
          <View style={styles.pickerContainer}>
            {categories.map(cat => (
              <TouchableOpacity
                key={cat}
                style={[
                  styles.categoryButton,
                  formData.category === cat && styles.categoryButtonSelected
                ]}
                onPress={() => setFormData({...formData, category: cat})}
              >
                <Text style={[
                  styles.categoryButtonText,
                  formData.category === cat && styles.categoryButtonTextSelected
                ]}>
                  {cat.replace('_', ' ')}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.label}>Advisor Name</Text>
          <TextInput
            style={styles.input}
            value={formData.advisorName}
            onChangeText={(text) => setFormData({...formData, advisorName: text})}
            placeholder="e.g., Warren Buffett, Ray Dalio, etc."
          />
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.label}>Title/Topic</Text>
          <TextInput
            style={styles.input}
            value={formData.title}
            onChangeText={(text) => setFormData({...formData, title: text})}
            placeholder="e.g., Value Investing Principles"
          />
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.label}>Knowledge Content</Text>
          <TextInput
            style={styles.textArea}
            value={formData.content}
            onChangeText={(text) => setFormData({...formData, content: text})}
            placeholder="Paste the detailed knowledge, strategies, principles, or advice here..."
            multiline
            numberOfLines={12}
          />
        </View>

        <TouchableOpacity 
          style={[styles.submitButton, loading && styles.buttonDisabled]}
          onPress={handleSubmit}
          disabled={loading}
        >
          <Text style={styles.submitButtonText}>
            {loading ? 'Storing...' : '📝 Store Knowledge'}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Test Section */}
      <View style={styles.testCard}>
        <Text style={styles.cardTitle}>Test Enhanced AI</Text>
        <Text style={styles.description}>
          Test if the AI can use the stored knowledge to provide better advice
        </Text>
        
        <TouchableOpacity 
          style={[styles.testButton, loading && styles.buttonDisabled]}
          onPress={testAdvice}
          disabled={loading}
        >
          <Text style={styles.testButtonText}>
            {loading ? 'Testing...' : '🤖 Test AI Advice'}
          </Text>
        </TouchableOpacity>
      </View>

      {/* AI Response Display */}
      {aiResponse && (
        <View style={styles.aiResponseCard}>
          <Text style={styles.cardTitle}>🤖 Enhanced AI Advice</Text>
          
          {/* Confidence & Sources */}
          <View style={styles.aiMetrics}>
            <View style={[styles.confidenceBadge, 
              aiResponse.confidence === 'high' ? styles.confidenceHigh :
              aiResponse.confidence === 'medium' ? styles.confidenceMedium : styles.confidenceLow
            ]}>
              <Text style={styles.confidenceText}>
                Confidence: {aiResponse.confidence.toUpperCase()}
              </Text>
            </View>
            <Text style={styles.sourcesText}>
              Sources Used: {aiResponse.sources?.length || 0}
            </Text>
          </View>

          {/* Sources */}
          {aiResponse.sources && aiResponse.sources.length > 0 && (
            <View style={styles.sourcesSection}>
              <Text style={styles.sourcesTitle}>📚 Knowledge Sources:</Text>
              {aiResponse.sources.map((source, index) => (
                <View key={index} style={styles.sourceItem}>
                  <Text style={styles.sourceTitle}>{source.title}</Text>
                  {source.advisor && (
                    <Text style={styles.sourceAdvisor}>by {source.advisor}</Text>
                  )}
                </View>
              ))}
            </View>
          )}

          {/* AI Advice */}
          <View style={styles.adviceSection}>
            <Text style={styles.adviceTitle}>💡 AI Advice:</Text>
            <Text style={styles.adviceText}>{aiResponse.advice}</Text>
          </View>
        </View>
      )}

      {/* Message Display */}
      {message && (
        <View style={styles.messageCard}>
          <Text style={styles.messageText}>{message}</Text>
        </View>
      )}

      {/* Sample Knowledge */}
      <View style={styles.sampleCard}>
        <Text style={styles.cardTitle}>📚 Sample Knowledge Format</Text>
        <Text style={styles.sampleText}>
          Title: "Diversification Beyond Traditional Assets"{'\n'}
          Advisor: "Ray Dalio"{'\n'}
          Content: "True diversification requires understanding correlation patterns across different economic environments. The All Weather portfolio concept allocates 30% stocks, 40% long-term bonds, 15% intermediate-term bonds, 7.5% commodities, and 7.5% REITs. This approach performs well across different economic cycles by balancing growth and inflation protection..."
        </Text>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  content: {
    maxWidth: 800,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 24,
    textAlign: 'center',
  },
  statsCard: {
    backgroundColor: '#EFF6FF',
    borderRadius: 16,
    padding: 20,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: '#DBEAFE',
  },
  formCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  testCard: {
    backgroundColor: '#F0FDF4',
    borderRadius: 16,
    padding: 20,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: '#BBF7D0',
  },
  sampleCard: {
    backgroundColor: '#FEF3C7',
    borderRadius: 16,
    padding: 20,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: '#FDE68A',
  },
  messageCard: {
    backgroundColor: '#F3F4F6',
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 16,
  },
  statText: {
    fontSize: 14,
    color: '#374151',
    marginBottom: 4,
  },
  inputGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    backgroundColor: '#FFFFFF',
  },
  textArea: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    backgroundColor: '#FFFFFF',
    minHeight: 200,
    textAlignVertical: 'top',
  },
  pickerContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  categoryButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    backgroundColor: '#F9FAFB',
  },
  categoryButtonSelected: {
    backgroundColor: '#2563EB',
    borderColor: '#2563EB',
  },
  categoryButtonText: {
    fontSize: 12,
    color: '#374151',
    textTransform: 'capitalize',
  },
  categoryButtonTextSelected: {
    color: '#FFFFFF',
  },
  submitButton: {
    backgroundColor: '#2563EB',
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
    marginTop: 8,
  },
  testButton: {
    backgroundColor: '#059669',
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
    marginTop: 8,
  },
  buttonDisabled: {
    backgroundColor: '#9CA3AF',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  testButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  description: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 12,
  },
  messageText: {
    fontSize: 14,
    color: '#374151',
  },
  sampleText: {
    fontSize: 12,
    color: '#92400E',
    lineHeight: 18,
    fontFamily: 'monospace',
  },
  aiResponseCard: {
    backgroundColor: '#F0FDF4',
    borderRadius: 16,
    padding: 24,
    marginBottom: 24,
    borderWidth: 2,
    borderColor: '#16A34A',
  },
  aiMetrics: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  confidenceBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  confidenceHigh: {
    backgroundColor: '#16A34A',
  },
  confidenceMedium: {
    backgroundColor: '#D97706',
  },
  confidenceLow: {
    backgroundColor: '#DC2626',
  },
  confidenceText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  sourcesText: {
    fontSize: 14,
    color: '#15803D',
    fontWeight: '500',
  },
  sourcesSection: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: '#DCFCE7',
    borderRadius: 8,
  },
  sourcesTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#15803D',
    marginBottom: 8,
  },
  sourceItem: {
    marginBottom: 4,
  },
  sourceTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#166534',
  },
  sourceAdvisor: {
    fontSize: 12,
    color: '#16A34A',
    fontStyle: 'italic',
  },
  adviceSection: {
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#BBF7D0',
  },
  adviceTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#15803D',
    marginBottom: 12,
  },
  adviceText: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 22,
  },
  accessDenied: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  accessDeniedText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#DC2626',
    marginBottom: 16,
    textAlign: 'center',
  },
  accessDeniedSubtext: {
    fontSize: 16,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 24,
  },
});

export default KnowledgeAdmin; 